import type { CanvasserStatsItem } from '@signalvote/shared';
import { CanvasserType } from '@signalvote/shared';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { cn } from '@/lib/utils';
import { getReliabilityColor } from './colorUtils';

interface SummaryCardsProps {
  canvasserStats: CanvasserStatsItem[];
  isLoading?: boolean;
  onReliabilityClick?: () => void;
  attentionCount?: number;
  onAttentionClick?: () => void;
}

export function SummaryCards({ canvasserStats, isLoading, onReliabilityClick, attentionCount, onAttentionClick }: SummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="card-interactive">
            <CardContent className="p-6">
              <Skeleton className="mb-2 h-4 w-24" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const total = canvasserStats.length;
  const volunteers = canvasserStats.filter(
    (c) => c.canvasserType === CanvasserType.Volunteer
  ).length;
  const paid = canvasserStats.filter(
    (c) => c.canvasserType === CanvasserType.Paid
  ).length;
  const totalDoors = canvasserStats.reduce((sum, c) => sum + c.doorsKnocked, 0);
  const totalContacts = canvasserStats.reduce((sum, c) => sum + c.contactsMade, 0);

  // Weighted reliability: sum(claimed - noShow) / sum(claimed) * 100
  const totalClaimed = canvasserStats.reduce((sum, c) => sum + c.shiftsClaimed, 0);
  const totalNoShow = canvasserStats.reduce((sum, c) => sum + c.shiftsNoShow, 0);
  const reliabilityRate = totalClaimed > 0
    ? Math.round((totalClaimed - totalNoShow) / totalClaimed * 1000) / 10
    : null;

  const reliabilityColor = getReliabilityColor(reliabilityRate);

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
      <Card className="card-interactive">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Total Canvassers</p>
          <p className="mt-1 text-3xl font-bold tracking-tight">{total}</p>
          <p className="text-sm text-muted-foreground">
            {volunteers} vol / {paid} paid
          </p>
        </CardContent>
      </Card>

      <Card
        className={cn(
          'card-interactive',
          onReliabilityClick && 'cursor-pointer hover:ring-2 hover:ring-primary/50',
        )}
        onClick={onReliabilityClick}
      >
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Shift Reliability</p>
          <p className={`mt-1 text-3xl font-bold tracking-tight ${reliabilityColor}`}>
            {reliabilityRate !== null ? `${reliabilityRate}%` : 'N/A'}
          </p>
        </CardContent>
      </Card>

      <Card className="card-interactive">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Doors Knocked</p>
          <p className="mt-1 text-3xl font-bold tracking-tight">{totalDoors}</p>
        </CardContent>
      </Card>

      <Card className="card-interactive">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Contacts Made</p>
          <p className="mt-1 text-3xl font-bold tracking-tight">{totalContacts}</p>
        </CardContent>
      </Card>

      {attentionCount !== undefined && (
        <Card
          className={cn(
            'card-interactive',
            onAttentionClick && 'cursor-pointer hover:ring-2 hover:ring-primary/50',
            attentionCount > 0 && 'border-l-4 border-destructive',
          )}
          onClick={onAttentionClick}
        >
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Attention Needed</p>
            <p className={`mt-1 text-3xl font-bold tracking-tight ${attentionCount > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
              {attentionCount}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
